import type { ReactNode } from 'react';

/**
 * Channel of the thread's MOST RECENT interaction. When provided, the avatar
 * renders a channel glyph (phone / chat bubble) in place of the initials so
 * the owner can tell calls from texts at a glance (Ernie 2026-06-05, PWA
 * Item 3). The per-contact color circle is preserved for visual identity.
 *   'call' → voice thread (last_message.source === 'shelby')
 *   'text' → SMS/other thread (last_message.source === 'sms' | 'system')
 */
export type AvatarChannel = 'call' | 'text';

interface AvatarProps {
  name: string;
  isUnknown?: boolean;
  overlay?: ReactNode;
  size?: number;
  /** When set, show a channel glyph instead of initials (Item 3). */
  channel?: AvatarChannel | null;
}

// Channel glyphs — sized relative to the circle (~0.5x). Phone for calls,
// chat bubble for texts. strokeWidth tuned to read crisply at 22px.
const channelGlyph: Record<AvatarChannel, ReactNode> = {
  call: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round" style={{ width: '50%', height: '50%' }}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  text: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round" style={{ width: '50%', height: '50%' }}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  ),
};

function initialsFrom(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return '?';
  // If it's a phone-number-only display ("(475) 555-0142"), bail to "?"
  if (/^[\s()+\-0-9]+$/.test(cleaned)) return '?';
  const parts = cleaned.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/**
 * Deterministic avatar color palette. P3.1 polish (Ernie 2026-05-03):
 * every contact gets a colored circle that's stable across sessions/views,
 * derived from a hash of the full name. Adds personality at zero cost and
 * makes the conversation/leads lists feel curated instead of stamped-out.
 *
 * Palette tuned for soft-on-light + readable-on-dark. Each entry is
 * a Tailwind class pair: [bg, text]. Add more by appending; the hash
 * just modulos into the array length so any size works.
 */
const AVATAR_PALETTE: Array<readonly [string, string]> = [
  ['bg-blue-100 text-blue-700', 'dark:bg-blue-900/40 dark:text-blue-200'],
  ['bg-emerald-100 text-emerald-700', 'dark:bg-emerald-900/40 dark:text-emerald-200'],
  ['bg-violet-100 text-violet-700', 'dark:bg-violet-900/40 dark:text-violet-200'],
  ['bg-rose-100 text-rose-700', 'dark:bg-rose-900/40 dark:text-rose-200'],
  ['bg-amber-100 text-amber-800', 'dark:bg-amber-900/40 dark:text-amber-200'],
  ['bg-cyan-100 text-cyan-700', 'dark:bg-cyan-900/40 dark:text-cyan-200'],
  ['bg-fuchsia-100 text-fuchsia-700', 'dark:bg-fuchsia-900/40 dark:text-fuchsia-200'],
  ['bg-indigo-100 text-indigo-700', 'dark:bg-indigo-900/40 dark:text-indigo-200'],
  ['bg-teal-100 text-teal-700', 'dark:bg-teal-900/40 dark:text-teal-200'],
  ['bg-lime-100 text-lime-800', 'dark:bg-lime-900/40 dark:text-lime-200'],
] as const;

/** djb2 hash — small, fast, decent distribution for short strings. */
function hashName(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = (h * 33) ^ s.charCodeAt(i);
  }
  // Force unsigned, then mod into palette range.
  return (h >>> 0) % AVATAR_PALETTE.length;
}

function colorClassesFor(name: string): string {
  const idx = hashName(name.trim().toLowerCase());
  const pair = AVATAR_PALETTE[idx] ?? AVATAR_PALETTE[0]!;
  return `${pair[0]} ${pair[1]}`;
}

// Unknown contacts (phone-only, no name) get a neutral slate so they
// visually read as "we don't know who this is yet."
const UNKNOWN_CLASSES = 'bg-slate-200 text-slate-500 dark:bg-[#334155] dark:text-[#94A3B8]';

export function Avatar({ name, isUnknown, overlay, size = 44, channel }: AvatarProps) {
  const initials = initialsFrom(name);
  const treatAsUnknown = isUnknown || initials === '?';

  const colorClasses = treatAsUnknown ? UNKNOWN_CLASSES : colorClassesFor(name);

  // When a channel is supplied, the glyph carries the meaning (call vs text),
  // so the circle gets a real aria-label instead of being decorative.
  const showChannel = channel != null;
  const channelLabel = channel === 'call' ? 'call' : 'text';

  return (
    <div
      className={[
        'relative flex flex-shrink-0 items-center justify-center rounded-full font-semibold',
        colorClasses,
      ].join(' ')}
      style={{ width: size, height: size, fontSize: '15px' }}
      {...(showChannel
        ? { role: 'img', 'aria-label': channelLabel }
        : { 'aria-hidden': true })}
    >
      {showChannel ? channelGlyph[channel] : <span>{initials}</span>}
      {overlay ? (
        <span className="absolute -bottom-px -right-px">{overlay}</span>
      ) : null}
    </div>
  );
}
